import { jsPDF } from 'jspdf';
import { displayDeck, cruiseLabel } from '../components/shared/helpers';
import { fetchCruiseNames } from '../lib/cruiseDetails';

// ─── Colors ───────────────────────────────────────────────────────────────────
const NAVY      = [10, 22, 40];     // #0A1628 — masthead + primary text
const GOLD      = [201, 168, 76];   // #C9A84C — masthead accent + grand total
const GREY      = [136, 147, 160];  // #8A93A0 — field labels
const BORDER    = [228, 231, 236];  // #E4E7EC — hairline borders
const TINT      = [244, 246, 248];  // #F4F6F8 — zebra row tint

// ─── Company fallback constants (used when `settings` is absent) ───────────────
const DEFAULTS = {
  company_name:   'Brahmaputra Cruise Private Limited',
  address:        '289, A T Road, Maa Padma Commercial Complex, Guwahati, Assam - 781009',
  gstin:          '18AACCB7255M1ZA',
  phone:          '9435558001 / 9854958549',
  email:          'info@bcpl.in',
  cruise_sac_code: '996415',
  food_sac_code:   '996331',
  cruise_gst_rate: 18,
  food_gst_rate:   5,
};

// ─── Logo loader — mirrors ticketPDF: draws '/alfresco-grand.png' to a canvas,
//     makes dark pixels transparent so the white script reads on the navy band,
//     resolves { dataUrl, aspect }. Non-fatal: resolves null on error, and in a
//     non-DOM environment (e.g. a Node test harness) so callers fall back cleanly.
export async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof Image === 'undefined') { resolve(null); return; }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        d[i + 3] = lum < 128 ? 0 : d[i + 3];
      }
      ctx.putImageData(id, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), aspect: img.width / img.height });
    };
    img.onerror = () => resolve(null);
    img.src = '/alfresco-grand.png';
  });
}

// ─── Numeric + date helpers ────────────────────────────────────────────────────
const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
// Indian-grouped, two-decimal money (no symbol — caller prefixes "Rs." where needed)
const num2 = (v) =>
  n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// GST percent — trims a trailing ".0" (18/2 -> "9%", 5/2 -> "2.5%")
const pct = (v) => `${Number(n(v).toFixed(2))}%`;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ordinal = (d) => { const s = ['th','st','nd','rd']; const v = d % 100; return d + (s[(v-20)%10] || s[v] || s[0]); };
// Accepts DD-MM-YYYY or YYYY-MM-DD, returns e.g. "5th Jul 2026"
const fmtDate = (raw) => {
  if (!raw) return '—';
  const p = String(raw).split('-');
  if (p.length !== 3) return String(raw);
  let dd, mm, yyyy;
  if (p[0].length === 4) [yyyy, mm, dd] = p;     // YYYY-MM-DD
  else                   [dd, mm, yyyy] = p;     // DD-MM-YYYY
  const mi = parseInt(mm, 10) - 1;
  if (mi < 0 || mi > 11) return String(raw);
  return `${ordinal(parseInt(dd, 10))} ${MONTHS[mi]} ${yyyy}`;
};

// ─── Amount in words (Indian numbering: crore / lakh / thousand) ───────────────
const ONES = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
  'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
const TENS = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
const twoDigit = (num) => {
  if (num < 20) return ONES[num];
  return (TENS[Math.floor(num / 10)] + (num % 10 ? ' ' + ONES[num % 10] : '')).trim();
};
const inWords = (num) => {
  if (num <= 0) return '';
  const parts = [];
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const hundred = Math.floor(num / 100); num %= 100;
  if (crore)    parts.push(inWords(crore).trim() + ' Crore');
  if (lakh)     parts.push(twoDigit(lakh) + ' Lakh');
  if (thousand) parts.push(twoDigit(thousand) + ' Thousand');
  if (hundred)  parts.push(ONES[hundred] + ' Hundred');
  if (num)      parts.push(twoDigit(num));
  return parts.join(' ');
};
const amountInWords = (amount) => {
  const rupees = Math.floor(n(amount));
  const paise = Math.round((n(amount) - rupees) * 100);
  let words = rupees > 0 ? `${inWords(rupees)} Rupees` : 'Zero Rupees';
  if (paise > 0) words += ` and ${twoDigit(paise)} Paise`;
  return `${words} Only`;
};

// ─── Generic table renderer (navy header, zebra rows, hairline outer border) ───
function drawTable(doc, x, y, cols, rows, rowH = 7) {
  const totalW = cols.reduce((s, c) => s + c.w, 0);

  // Header band
  doc.setFillColor(...NAVY);
  doc.rect(x, y, totalW, rowH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  let cx = x;
  cols.forEach((c) => {
    const right = c.align === 'right';
    doc.text(c.label, right ? cx + c.w - 2 : cx + 2, y + rowH - 2.4, { align: right ? 'right' : 'left' });
    cx += c.w;
  });

  // Body rows
  let ry = y + rowH;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  rows.forEach((r, idx) => {
    if (idx % 2 === 1) { doc.setFillColor(...TINT); doc.rect(x, ry, totalW, rowH, 'F'); }
    let cxx = x;
    cols.forEach((c) => {
      const right = c.align === 'right';
      const val = r[c.key] == null ? '' : String(r[c.key]);
      doc.setTextColor(...NAVY);
      doc.text(val, right ? cxx + c.w - 2 : cxx + 2, ry + rowH - 2.4, { align: right ? 'right' : 'left' });
      cxx += c.w;
    });
    ry += rowH;
  });

  // Outer border
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.rect(x, y, totalW, ry - y);
  return ry;
}

// ─── Shared doc builder — computes + renders the exact invoice used everywhere ─
export async function buildInvoiceDoc(invoice, booking = {}, settings = null, pricingRates = null, preloadedLogo = null) {
  const cfg = { ...DEFAULTS, ...(settings || {}) };
  const logo = preloadedLogo || (await loadLogoDataUrl());
  const rates = { adult: n(pricingRates?.adult), child: n(pricingRates?.child) };

  const isB2B = invoice.invoice_type === 'b2b';
  const cruiseType = invoice.cruise_type || booking.cruise_type || '—';
  // Display label only (Dinner Cruise → "Performance Deck"); the stored deck is untouched.
  const deck = displayDeck(cruiseType, invoice.deck || booking.deck) || '—';
  const isDinner = String(cruiseType).toLowerCase().includes('dinner');
  // Guest-facing SHORT name for the single details line (the long one overflows
  // once passengers are listed). Display only; cruiseType stays the key above.
  const cruiseNames = await fetchCruiseNames({ cruiseName: invoice.cruise_type || booking.cruise_type || null });
  const cruiseLine = cruiseLabel(cruiseNames, 'short') || cruiseType;
  const adults = n(invoice.adults ?? booking.adults);
  const children = n(invoice.children ?? booking.children);
  const cruiseRate = n(cfg.cruise_gst_rate) || 18;
  const foodRate = n(cfg.food_gst_rate) || 5;

  // Stored (NET) taxables — for B2B these are already discounted.
  const netCruise = n(invoice.taxable_amount_cruise);
  const netFood = n(invoice.taxable_amount_food);
  const netTaxable = netCruise + netFood;

  // Effective discount factor from the stored amounts (reliable; matches the rest
  // of the system). For B2C fullTotal === grand_total, so factor === 1 and the
  // displayed rates equal full price (unchanged output).
  const fullTotal = n(invoice.total_amount);
  const netGrand = n(invoice.grand_total);
  const factor = fullTotal > 0 ? netGrand / fullTotal : 1;

  // ── ITEM ROWS (DISCOUNTED per-head rates for display; no discount block) ─────
  const items = [];
  if (isDinner) {
    const qty = adults + children;
    // Show the stored NET per-head taxables directly (already discounted for B2B).
    items.push({ desc: 'Cruise Charges', sac: cfg.cruise_sac_code, qty, rate: qty ? netCruise / qty : 0, amount: netCruise });
    items.push({ desc: 'Food & Beverage', sac: cfg.food_sac_code, qty, rate: qty ? netFood / qty : 0, amount: netFood });
  } else {
    // Full per-head taxable (rate ex-GST) scaled by the discount factor.
    const adultTaxableDisc = (rates.adult / (1 + cruiseRate / 100)) * factor;
    const childTaxableDisc = (rates.child / (1 + cruiseRate / 100)) * factor;
    if (adults > 0)
      items.push({ desc: 'Cruise Charges - Adult', sac: cfg.cruise_sac_code, qty: adults, rate: adultTaxableDisc, amount: adults * adultTaxableDisc });
    if (children > 0)
      items.push({ desc: 'Cruise Charges - Child', sac: cfg.cruise_sac_code, qty: children, rate: childTaxableDisc, amount: children * childTaxableDisc });
  }

  // ── TAX ROWS (net stored values) ────────────────────────────────────────────
  const taxRows = [];
  if (isDinner) {
    taxRows.push({ desc: 'Cruise Charges', taxable: netCruise, gstPct: cruiseRate / 2, cgst: n(invoice.cgst_cruise), sgst: n(invoice.sgst_cruise) });
    taxRows.push({ desc: 'Food & Beverage', taxable: netFood, gstPct: foodRate / 2, cgst: n(invoice.cgst_food), sgst: n(invoice.sgst_food) });
  } else {
    taxRows.push({ desc: 'Cruise Charges', taxable: netCruise, gstPct: cruiseRate / 2, cgst: n(invoice.cgst_cruise), sgst: n(invoice.sgst_cruise) });
  }

  // Totals
  const totalCgst = n(invoice.cgst_cruise) + n(invoice.cgst_food);
  const totalSgst = n(invoice.sgst_cruise) + n(invoice.sgst_food);
  const totalTax = n(invoice.total_tax);
  const grandTotal = n(invoice.grand_total);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 15, CW = W - 2 * M;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, H, 'F');

  // 1. HEADER — navy band, gold rule; TAX INVOICE (left), company block (right)
  const bandH = 34;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, bandH, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(0, bandH - 0.6, W, 0.6, 'F');

  if (logo?.dataUrl) {
    // Logo top-left (white script reads on navy), TAX INVOICE beneath it.
    const lh = 12, lw = lh * logo.aspect;
    doc.addImage(logo.dataUrl, 'PNG', M, 6, lw, lh);
    doc.setTextColor(...GOLD);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setCharSpace(1.4);
    doc.text('TAX INVOICE', M, 25);
    doc.setCharSpace(0);
  } else {
    // Text fallback (logo failed / non-DOM): TAX INVOICE + unit line.
    doc.setTextColor(...GOLD);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setCharSpace(1.4);
    doc.text('TAX INVOICE', M, 15);
    doc.setCharSpace(0);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('Alfresco Grand — A unit of Brahmaputra Cruise Pvt. Ltd.', M, 22);
  }

  let hy = 8;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text(cfg.company_name, W - M, hy, { align: 'right' });
  hy += 4.4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.splitTextToSize(cfg.address, 92).forEach((l) => { doc.text(l, W - M, hy, { align: 'right' }); hy += 3.3; });
  doc.text(`GSTIN: ${cfg.gstin}`, W - M, hy, { align: 'right' }); hy += 3.3;
  doc.text(`Ph: ${cfg.phone}`, W - M, hy, { align: 'right' }); hy += 3.3;
  doc.text(cfg.email, W - M, hy, { align: 'right' });

  // 2. INVOICE DETAILS (left) + BILL TO (right)
  let y = 42;
  const colGap = 10, colW = (CW - colGap) / 2, rightX = M + colW + colGap;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.setCharSpace(0.5);
  doc.text('INVOICE DETAILS', M, y);
  doc.text('BILL TO', rightX, y);
  doc.setCharSpace(0);
  y += 5.5;

  // Left column
  let ly = y;
  const leftRows = [
    ['Invoice No:', invoice.invoice_number || '—'],
    ['Invoice Date:', fmtDate(invoice.invoice_date)],
    ['Place of Supply:', 'Assam (18)'],
  ];
  doc.setFontSize(8.5);
  leftRows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...GREY);
    doc.text(k, M, ly);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY);
    doc.text(String(v), M + 26, ly);
    ly += 5;
  });

  // Right column — B2B partner block vs B2C customer block
  let ry = y;
  if (isB2B) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...NAVY);
    doc.text(invoice.partner_legal_name || '—', rightX, ry); ry += 4.6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    if (invoice.partner_address) {
      String(invoice.partner_address).split('\n').forEach((line) => {
        doc.splitTextToSize(line, colW).forEach((wl) => { doc.text(wl, rightX, ry); ry += 3.7; });
      });
    }
    if (invoice.partner_state) {
      doc.text(`${invoice.partner_state} (${invoice.partner_state_code || ''})`.trim(), rightX, ry); ry += 3.7;
    }
    doc.setFont('helvetica', 'bold');
    doc.text(`GSTIN: ${invoice.partner_gstin || '—'}`, rightX, ry); ry += 3.7;
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...NAVY);
    doc.text(invoice.customer_name || '—', rightX, ry); ry += 4.6;
    if (invoice.customer_phone) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(`Phone: ${invoice.customer_phone}`, rightX, ry); ry += 3.7;
    }
  }
  y = Math.max(ly, ry) + 3;

  // 3. BOOKING DETAILS — tinted single-line band
  const paxParts = [];
  if (adults > 0) paxParts.push(`${adults} Adults`);
  if (children > 0) paxParts.push(`${children} Children`);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.setFillColor(...TINT);
  doc.rect(M, y, CW, 9, 'F');
  doc.rect(M, y, CW, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...NAVY);
  const bookingLine =
    `Cruise: ${cruiseLine}    |    Deck: ${deck}    |    Date of Cruise: ${fmtDate(booking.date)}    |    Passengers: ${paxParts.join(', ') || '—'}`;
  doc.text(bookingLine, M + 3, y + 5.7);
  y += 9 + 7;

  // 4. ITEM TABLE
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GREY);
  doc.setCharSpace(0.5); doc.text('ITEMS', M, y); doc.setCharSpace(0); y += 3;
  const itemCols = [
    { key: 'desc',   label: 'Description',       w: 66, align: 'left' },
    { key: 'sac',    label: 'SAC',               w: 24, align: 'left' },
    { key: 'qty',    label: 'Qty',               w: 16, align: 'right' },
    { key: 'rate',   label: 'Rate (excl. GST)',  w: 37, align: 'right' },
    { key: 'amount', label: 'Amount',            w: 37, align: 'right' },
  ];
  const itemRows = items.map((it) => ({
    desc: it.desc, sac: it.sac || '—', qty: String(it.qty),
    rate: num2(it.rate), amount: num2(it.amount),
  }));
  y = drawTable(doc, M, y, itemCols, itemRows, 7) + 5;

  // 5. TAX TABLE
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...GREY);
  doc.setCharSpace(0.5); doc.text('TAX DETAILS', M, y); doc.setCharSpace(0); y += 3;
  const taxCols = [
    { key: 'desc',    label: 'Description',   w: 50, align: 'left' },
    { key: 'taxable', label: 'Taxable Amt',   w: 28, align: 'right' },
    { key: 'cgstpct', label: 'CGST %',        w: 18, align: 'right' },
    { key: 'cgst',    label: 'CGST Amt',      w: 24, align: 'right' },
    { key: 'sgstpct', label: 'SGST %',        w: 18, align: 'right' },
    { key: 'sgst',    label: 'SGST Amt',      w: 24, align: 'right' },
    { key: 'total',   label: 'Total Tax',     w: 18, align: 'right' },
  ];
  const taxTableRows = taxRows.map((r) => ({
    desc: r.desc,
    taxable: num2(r.taxable),
    cgstpct: pct(r.gstPct),
    cgst: num2(r.cgst),
    sgstpct: pct(r.gstPct),
    sgst: num2(r.sgst),
    total: num2(r.cgst + r.sgst),
  }));
  y = drawTable(doc, M, y, taxCols, taxTableRows, 7) + 6;

  // 6. TOTALS — right-aligned block, gold grand-total band
  const tbLabelX = M + CW - 70;
  const totalsRows = [
    ['Taxable Amount', num2(netTaxable)],
    ['Total CGST', num2(totalCgst)],
    ['Total SGST', num2(totalSgst)],
    ['Total Tax', num2(totalTax)],
  ];
  doc.setFontSize(8.5);
  totalsRows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...GREY);
    doc.text(k, tbLabelX, y);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY);
    doc.text(v, M + CW, y, { align: 'right' });
    y += 5;
  });
  y += 1.5;
  doc.setFillColor(...NAVY);
  // Right edge clamped to the content margin (M + CW), aligning with the tables/
  // totals rows above so the band never bleeds past the right margin.
  const gtBoxX = tbLabelX - 4;
  doc.rect(gtBoxX, y - 4, (M + CW) - gtBoxX, 9, 'F');
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('GRAND TOTAL', tbLabelX - 1, y + 1.8);
  doc.text(`Rs. ${num2(grandTotal)}`, M + CW - 1, y + 1.8, { align: 'right' });
  y += 12;

  // 7. AMOUNT IN WORDS
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...NAVY);
  const wordsLabel = 'Amount in Words: ';
  doc.text(wordsLabel, M, y);
  const wordsX = M + doc.getTextWidth(wordsLabel);
  doc.setFont('helvetica', 'normal');
  const wrapped = doc.splitTextToSize(amountInWords(grandTotal), CW - doc.getTextWidth(wordsLabel));
  wrapped.forEach((wl, i) => doc.text(wl, i === 0 ? wordsX : M, y + i * 4));

  // 8. FOOTER — anchored near the bottom edge
  const fyPos = 282;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, fyPos - 7, W - M, fyPos - 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text('This is a computer generated invoice.', W / 2, fyPos, { align: 'center' });
  doc.text('Thank you for choosing Alfresco Grand', W / 2, fyPos + 4, { align: 'center' });
  doc.text(`${cfg.company_name} | GSTIN: ${cfg.gstin}`, W / 2, fyPos + 8, { align: 'center' });

  const rawNumber = invoice.invoice_number ? String(invoice.invoice_number) : 'Invoice';
  const filename = `Invoice-${rawNumber.replace(/\//g, '-')}.pdf`;

  return { doc, filename };
}

// ─── Download (save to disk) ───────────────────────────────────────────────────
export async function downloadInvoice(invoice, booking, settings, pricingRates, preloadedLogo) {
  const { doc, filename } = await buildInvoiceDoc(invoice, booking, settings, pricingRates, preloadedLogo);
  doc.save(filename);
}

// ─── Same invoice as base64 — for send/upload flows (no download) ──────────────
export async function getInvoicePdfBase64(invoice, booking, settings, pricingRates, preloadedLogo) {
  const { doc, filename } = await buildInvoiceDoc(invoice, booking, settings, pricingRates, preloadedLogo);
  const dataUri = doc.output('datauristring'); // "data:application/pdf;...;base64,XXXX"
  const base64 = dataUri.substring(dataUri.indexOf(',') + 1);
  return { base64, filename };
}
